import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, FileSignature, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { playDing } from "../lib/notifySound";

export const ShareOpenNotifier = () => {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const known = useRef(new Set());
  const on = !!user && !["cliente", "superadmin"].includes(user.role) && can("pratiche");

  const show = useCallback((e) => {
    const seen = () => { api.post(`/studio/condivisioni/aperture/${e.id}/visto`).catch(() => {}); };
    const reply = e.tipo === "risposta";
    const firma = e.tipo === "firma";
    const pro = e.tipo === "professionista";
    if (firma || pro) {
      toast.custom((t) => (
        <div className="w-[360px] max-w-[90vw] rounded-xl border border-[#a7f3d0] bg-white p-4 shadow-xl flex items-start gap-3" data-testid="firma-signed-popup">
          <div className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center bg-emerald-50"><FileSignature className="h-4 w-4 text-emerald-600" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#111827] break-words" data-testid={pro ? "pro-event-popup-text" : "firma-signed-popup-text"}>{pro ? `${e.ruolo} ${e.nome} — pratica ${e.numero_pratica}` : `${e.nome} ha firmato il documento della pratica ${e.numero_pratica}`}</p>
            <p className="mt-1 text-xs text-[#4b5563] break-words">{pro ? e.testo : `“${e.testo}” — copia firmata salvata negli allegati`}</p>
            <button type="button" data-testid="firma-signed-open-pratica" onClick={() => { seen(); toast.dismiss(t); navigate(`/app/pratiche?p=${e.pratica_id}`); }}
              className="mt-2 rounded-md bg-[#047857] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#065f46] transition-colors">Apri pratica</button>
          </div>
          <button type="button" onClick={() => { seen(); toast.dismiss(t); }} data-testid="firma-signed-close" className="text-[#6b7280] hover:text-[#111827]" aria-label="Chiudi"><X className="h-4 w-4" /></button>
        </div>
      ), { position: "bottom-right", duration: Infinity });
      return;
    }
    toast.custom((t) => (
      <div className="w-[360px] max-w-[90vw] rounded-xl border border-[#e5e7eb] bg-white p-4 shadow-xl flex items-start gap-3" data-testid={reply ? "share-reply-popup" : "share-open-popup"}>
        <div className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${reply ? "bg-amber-50" : "bg-emerald-50"}`}>{reply ? <MessageSquare className="h-4 w-4 text-amber-600" /> : <Eye className="h-4 w-4 text-emerald-600" />}</div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#111827] break-words" data-testid="share-open-popup-text">
            {e.nome}{e.ruolo ? ` (${e.ruolo})` : ""} {reply ? "ha scritto dal link di condivisione della pratica" : "ha visualizzato gli allegati della pratica"} {e.numero_pratica}
          </p>
          {reply && e.testo && <p className="mt-1 text-xs text-[#4b5563] line-clamp-3 break-words whitespace-pre-wrap" data-testid="share-reply-popup-text">“{e.testo}”</p>}
          <button type="button" data-testid="share-open-pratica" onClick={() => { toast.dismiss(t); navigate(`/app/pratiche?p=${e.pratica_id}`); }}
            className="mt-2 rounded-md bg-[#1d4ed8] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1e40af] transition-colors">Apri pratica</button>
        </div>
        <button type="button" onClick={() => toast.dismiss(t)} data-testid="share-open-close" className="text-[#6b7280] hover:text-[#111827]" aria-label="Chiudi"><X className="h-4 w-4" /></button>
      </div>
    ), { position: "bottom-right", duration: reply ? 15000 : 8000, onDismiss: seen, onAutoClose: seen });
  }, [navigate]);

  const poll = useCallback(() => {
    api.get("/studio/condivisioni/aperture").then(({ data }) => {
      const fresh = (data || []).filter((e) => !known.current.has(e.id));
      if (!fresh.length) return;
      fresh.forEach((e) => known.current.add(e.id));
      playDing();
      fresh.forEach(show);
    }).catch(() => {});
  }, [show]);

  useEffect(() => {
    if (!on) return;
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [on, poll]);

  return null;
};
